import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown, ChevronUp, ClipboardCheck, FileText, Lock, Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError, openProtectedFile } from "@/lib/api";
import AdminGoalPicker from "@/components/AdminGoalPicker";

const EV_STATUS = {
  pending: ["بانتظار المراجعة", "bg-amber-100 text-amber-800 border-amber-300"],
  accepted: ["مقبول", "bg-emerald-100 text-emerald-800 border-emerald-300"],
  rejected: ["مرفوض", "bg-rose-100 text-rose-700 border-rose-300"],
};

export default function AdminEvalManager({ empId, period, onReviewed }) {
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openGoal, setOpenGoal] = useState(null);
  const [rejectNote, setRejectNote] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin-eval/${empId}`, { params: { period } });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, [empId, period]);

  useEffect(() => { setData(null); setEditing(false); setOpenGoal(null); load(); }, [load]);
  useEffect(() => {
    api.get("/admin-eval/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const saveGoals = async () => {
    setSaving(true);
    try {
      await api.put(`/admin-eval/${empId}/goals`, { period, goal_ids: picked });
      toast.success("تم حفظ الأهداف وقفلها");
      setEditing(false);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const review = async (evId, action) => {
    try {
      await api.post("/evidence/review", { evidence_id: evId, action, note: rejectNote[evId] || "" });
      toast.success(action === "accept" ? "تم قبول الشاهد وحُدّث التقييم" : "تم رفض الشاهد");
      await load();
      if (onReviewed) onReviewed();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!data) {
    return <div className="bg-white rounded-xl border p-10 text-center text-slate-400 text-sm">جارٍ التحميل...</div>;
  }

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="admin-eval-manager">
      <div className="bg-emerald-950 text-white px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <p className="font-extrabold text-sm" data-testid="admin-eval-title">
          نموذج تقييم إداري — {data.employee.name}
        </p>
        <p className="text-[11px] text-emerald-300/80">{period} — {data.year}</p>
      </div>

      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-3 bg-sky-50/50"
        data-testid="admin-eval-criterion1">
        <Lock className="w-4 h-4 text-sky-700" />
        <p className="text-xs font-bold flex-1">أداء الواجبات الوظيفية (حصة متساوية إضافية)</p>
        <span className="text-[11px] font-bold text-sky-800 bg-sky-100 border border-sky-300 rounded-full px-2.5 py-0.5"
          data-testid="admin-c1-score">آلي: {data.criterion1.score}/5</span>
        <span className="text-[10px] text-slate-400">{data.criterion1.basis}</span>
      </div>

      {!data.selected && !editing && (
        <div className="p-8 text-center space-y-3" data-testid="admin-eval-empty">
          <ClipboardCheck className="w-8 h-8 mx-auto text-slate-300" />
          <p className="text-sm font-bold text-slate-600">لم يختر هذا الإداري أهدافه بعد لهذه الفترة</p>
          <p className="text-[11px] text-slate-400">يختارها من بوابته، أو يمكنك تحديدها له مباشرة</p>
          <Button size="sm" onClick={() => { setPicked([]); setEditing(true); }}
            data-testid="admin-goals-set-btn"
            className="h-9 text-xs bg-emerald-900 hover:bg-emerald-950 text-white">
            تحديد الأهداف له
          </Button>
        </div>
      )}

      {editing && catalog && (
        <div className="p-4">
          <AdminGoalPicker catalog={catalog} picked={picked} setPicked={setPicked}
            onSave={saveGoals} onCancel={() => setEditing(false)} saving={saving}
            testIdPrefix="admin-goal" />
        </div>
      )}

      {data.selected && !editing && (
        <>
          <div className="divide-y" data-testid="admin-goals-list">
            {data.goals.map((g, gi) => (
              <div key={g.goal.id} className="px-4 py-3" data-testid={`admin-goal-row-${g.goal.id}`}>
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-[11px] font-bold flex items-center justify-center shrink-0">{gi + 1}</span>
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-[13px] font-bold leading-6">{g.goal.title}</p>
                    <p className="text-[11px] text-slate-400 leading-5">{g.goal.desc}</p>
                  </div>
                  <span className="text-[11px] font-bold text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5"
                    data-testid={`admin-goal-score-${g.goal.id}`}>تلقائي: {g.score}/5</span>
                  {g.evidence.filter((e) => e.status === "pending").length > 0 && (
                    <span data-testid={`admin-goal-pending-${g.goal.id}`}
                      className="h-5 px-1.5 rounded-full bg-rose-600 text-white text-[10px] font-extrabold flex items-center justify-center">
                      {g.evidence.filter((e) => e.status === "pending").length} جديد
                    </span>
                  )}
                  {data.self_scores?.[g.goal.id] != null && (
                    <span className="text-[11px] font-bold text-sky-900 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-0.5"
                      data-testid={`admin-goal-self-${g.goal.id}`}>ذاتي: {data.self_scores[g.goal.id]}/5</span>
                  )}
                  <button onClick={() => setOpenGoal(openGoal === g.goal.id ? null : g.goal.id)}
                    data-testid={`admin-goal-evidence-toggle-${g.goal.id}`}
                    className="flex items-center gap-1 text-[11px] font-bold text-emerald-800 hover:underline">
                    <FileText className="w-3.5 h-3.5" /> الشواهد ({g.evidence.length})
                    {openGoal === g.goal.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                </div>
                {openGoal === g.goal.id && (
                  <div className="mt-2 mr-9 space-y-1.5" data-testid={`admin-goal-evidence-${g.goal.id}`}>
                    {g.evidence.length === 0 && (
                      <p className="text-[11px] text-slate-400">لم تُرفع شواهد لهذا الهدف بعد</p>)}
                    {g.evidence.map((ev) => {
                      const st = EV_STATUS[ev.status];
                      return (
                        <div key={ev.id} className="flex flex-wrap items-center gap-2 rounded-lg border bg-slate-50/60 px-3 py-2"
                          data-testid={`admin-evidence-item-${ev.id}`}>
                          <p className="flex-1 min-w-[160px] text-[11px] font-semibold text-slate-700">
                            {ev.original_name || "شاهد مرفوع"}</p>
                          <button type="button" onClick={() => openProtectedFile(`/evidence/file/${ev.id}`)}
                            data-testid={`admin-evidence-view-${ev.id}`}
                            className="text-[11px] font-bold text-sky-700 hover:underline">عرض الملف</button>
                          <span className={`text-[10px] font-bold border rounded-full px-2 py-0.5 ${st[1]}`}
                            data-testid={`admin-evidence-status-${ev.id}`}>{st[0]}</span>
                          {ev.status !== "accepted" && (
                            <button onClick={() => review(ev.id, "accept")}
                              data-testid={`admin-evidence-accept-${ev.id}`}
                              className="flex items-center gap-0.5 text-[11px] font-bold text-emerald-700 hover:text-emerald-900">
                              <Check className="w-3.5 h-3.5" /> قبول
                            </button>
                          )}
                          {ev.status !== "rejected" && (
                            <button onClick={() => review(ev.id, "reject")}
                              data-testid={`admin-evidence-reject-${ev.id}`}
                              className="flex items-center gap-0.5 text-[11px] font-bold text-rose-600 hover:text-rose-800">
                              <X className="w-3.5 h-3.5" /> رفض
                            </button>
                          )}
                          {ev.status === "pending" && (
                            <input value={rejectNote[ev.id] || ""} placeholder="ملاحظة الرفض (اختياري)"
                              data-testid={`admin-evidence-note-${ev.id}`}
                              onChange={(e) => setRejectNote({ ...rejectNote, [ev.id]: e.target.value })}
                              className="h-7 w-36 rounded-md border px-2 text-[11px]" />
                          )}
                          {ev.status === "rejected" && ev.note && (
                            <span className="text-[10px] text-rose-600">ملاحظة: {ev.note}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="border-t bg-slate-50/60 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="admin-eval-totals">
            <span className="text-xs font-bold text-emerald-900 bg-emerald-100 border border-emerald-200 rounded-full px-3 py-1"
              data-testid="admin-eval-total">
              التقييم النهائي (تلقائي): {data.total ?? "—"} / 5 {data.pct != null && `(${data.pct}٪)`}
            </span>
            <span className="text-xs font-bold text-sky-900 bg-sky-50 border border-sky-200 rounded-full px-3 py-1"
              data-testid="admin-eval-self-total">
              التقييم الذاتي: {data.self_total != null ? `${data.self_total} / 5 (${data.self_pct}٪)` : "لم يُعبَّأ بعد"}
            </span>
            {data.self_total != null && data.total != null && (
              <span className="text-xs font-bold text-slate-700 bg-white border rounded-full px-3 py-1"
                data-testid="admin-eval-diff">الفرق: {Math.round((data.pct - data.self_pct) * 10) / 10}٪</span>
            )}
            <Button size="sm" variant="outline" onClick={() => { setPicked(data.goals.map((g) => g.goal.id)); setEditing(true); }}
              data-testid="admin-goals-edit-btn" className="h-9 text-xs gap-1.5 mr-auto">
              <Pencil className="w-3.5 h-3.5" /> تعديل الأهداف
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
